import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lightning, Crown, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";

/**
 * Small credits pill in the dashboard header.
 * Shows "N credits" with a crown icon + links to Pricing.
 * Turns red/urgent when low.
 */
export default function CreditsPill({ onUpgrade, refreshKey = 0 }) {
  const [credits, setCredits] = useState(null);
  const [tier, setTier] = useState("free");
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setCredits(data.credits);
      setTier(data.subscription_tier || "free");
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Refetch every 60s to keep in sync with any background credit consumption
    const t = setInterval(fetchStatus, 60_000);
    return () => clearInterval(t);
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-zinc-100 text-zinc-400 text-xs">
        <CircleNotch size={12} className="animate-spin" />
      </div>
    );
  }

  // Admin / unlimited
  if (credits === -1) {
    return (
      <div
        data-testid="credits-pill"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-amber-400/15 text-amber-700 text-xs font-semibold"
      >
        <Crown size={12} weight="fill" /> Admin
      </div>
    );
  }

  const isLow = credits <= 10;
  const isOut = credits <= 0;

  return (
    <button
      onClick={onUpgrade}
      data-testid="credits-pill"
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.02] ${
        isOut
          ? "bg-rose-500 text-white"
          : isLow
          ? "bg-amber-400 text-zinc-900 animate-pulse"
          : tier === "free"
          ? "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
          : "bg-[#E91E8C]/10 text-[#E91E8C] hover:bg-[#E91E8C]/15"
      }`}
    >
      <Lightning size={12} weight="fill" />
      <span data-testid="credits-pill-count">{credits} credit{credits === 1 ? "" : "s"}</span>
      {tier === "free" && (
        <>
          <span className="mx-0.5 opacity-50">·</span>
          <span className="flex items-center gap-0.5">
            Upgrade <ArrowRight size={10} />
          </span>
        </>
      )}
    </button>
  );
}
